import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { MaterialModule } from 'src/app/material.module';
import { LookupService } from 'src/app/services/lookup.service';
import { ClinicalItemService } from '../clinical-item.service';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IClinicalItem } from '../iclinical-item';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { IClinicalCategory } from '../../clicnical-category/iclinical-category';
import { IClinicalSubCategory } from '../../clicnical-sub-category/iclinical-sub-category';
import { ClinicalCategoryDetailsComponent } from '../../clicnical-category/detail/clinicalcategory-detail.component';
import { ClinicalSubCategoryDetailsComponent } from '../../clicnical-sub-category/detail/clinical-sub-category-detail.component';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';

@Component({
  selector: 'app-clinicalitem-detail',
  standalone: true,
  imports: [MaterialModule, CommonModule, TablerIconsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './clinicalitem-detail.component.html',
  styleUrl: './clinicalitem-detail.component.scss'
})
export class ClinicalitemDetailComponent implements OnInit {

  clinicalItemForm: FormGroup;
  isEdit: boolean;
  fromOPD: boolean = false;
  selectedCategoryId: number;

  clinicalCategoryddl: CommonDropDown[] = [];
  filteredclinicalCategory$: Observable<CommonDropDown[]>;
  clinicalCategoryCtrl = new FormControl();
  showAddOptionforCategory = false;

  clinicalSubCategoryddl: CommonDropDown[] = [];
  filteredclinicalSubCategory$: Observable<CommonDropDown[]>;
  clinicalSubCategoryCtrl = new FormControl();
  showAddOptionforSubCategory = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _lookupService: LookupService,
    private _formBuilder: FormBuilder,
    private _itemService: ClinicalItemService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<ClinicalitemDetailComponent>,
    private dialog: MatDialog,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private _dataupdate: { clinicalitems: IClinicalItem, isEdit: boolean, fromOPDcategoryvalue: any, fromOPDSubcategoryvalue: any },
  ) {
    this.isEdit = _dataupdate?.isEdit;
  }

  ngOnInit(): void {
    this.clinicalItemForm = this._formBuilder.group({
      categoryId: [null, [Validators.required]],
      subcategoryId: [null],
      description: ['', [Validators.required, Validators.maxLength(255)]],
      additionalInfo: ['', [Validators.maxLength(255)]],
      isSince: [false],
      SeqNo: [1]
    })

    this.loadCategories();

    if (this.isEdit && this._dataupdate.clinicalitems?.subCategoryId) {
      this.loadSubCategoriesAndSet(
        this._dataupdate.clinicalitems.categoryId,
        this._dataupdate.clinicalitems.subCategoryId
      );
    }
  }

  private loadCategories(): void {
    this._lookupService.getCategory()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.clinicalCategoryddl = data;
        this.initializeCategoryAutocomplete();
        this.clinicalItemDatafill();
      });
  }

  private initializeCategoryAutocomplete(): void {
    this.filteredclinicalCategory$ = this.clinicalCategoryCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      map(value => {
        const search = typeof value === 'string' ? value : value?.name || '';

        const filtered = this.clinicalCategoryddl.filter(x =>
          x.name.toLowerCase().includes(search.toLowerCase())
        );

        this.showAddOptionforCategory =
          !!search && !this.clinicalCategoryddl.some(x =>
            x.name.toLowerCase() === search.toLowerCase()
          );

        return filtered;
      })
    );
  }

  onclinicalItemSave() {
    const clinicalitem = this.clinicalItemForm.getRawValue();
    this._itemService.createclinicalItem(clinicalitem).subscribe((response) => {
      if (response.isSuccess) {
        this.dialogRef.close(response);
        this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.CLINICALITEM')));
      }
      else {
        const allErrors = Object.values(response.errors).flat();
        this.toastr.error(allErrors.join("\n"));
      }
    });

  }

  onclinicalItemsUpdate() {
    const clinicalItems =
    {
      ...this.clinicalItemForm.getRawValue(),
      id: this._dataupdate?.clinicalitems?.id
    };
    this._itemService.updateClinicalItem(clinicalItems).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.CLINICALITEM')));
        }
      },
      error: (error) => {
        if (error?.error?.errors) {
          const allErrors = Object.values(error.error.errors).flat();
          this.toastr.error(allErrors.join("\n"));
        } else {
          this.toastr.error('Something went wrong');
        }
      }
    });

  }

  onCloseDialog() {
    this.dialogRef.close();
  }

  displayCategory(category: IClinicalCategory): string {
    return category && category.name ? category.name : '';
  }

  displaySubCategory(subcategory: IClinicalSubCategory): string {
    return subcategory && subcategory.name ? subcategory.name : '';
  }

  onClinicalCategorySelected(category: IClinicalCategory) {
    this.clinicalItemForm.patchValue({
      categoryId: category.id
    });

    this.clinicalCategoryCtrl.setValue(category);
    this.loadSubcategories(category.id);
  }

  onClinicalSubCategorySelected(subcategory: IClinicalSubCategory) {
    this.clinicalItemForm.patchValue({
      subcategoryId: subcategory.id
    });
    this.clinicalSubCategoryCtrl.setValue(subcategory);
  }

  addClinicalCategory(category: string) {
    this.dialog.open(ClinicalCategoryDetailsComponent, {
      width: '500px',
      height: 'auto',
      data: { categoryValueFromItem: category }
    })
      .afterClosed()
      .subscribe(response => {
        if (!response) return;

        const addedCategory = response.result;

        this.clinicalCategoryddl.push(addedCategory);

        this.clinicalCategoryCtrl.setValue(addedCategory);
        this.clinicalItemForm.patchValue({ categoryId: addedCategory.id });

        this.loadSubcategories(addedCategory.id);

        this.clinicalSubCategoryCtrl.setValue('');
        this.clinicalItemForm.patchValue({ subcategoryId: null });
      });
  }

  addClinicalSubCategory(subcategory: string, categoryId: number) {
    this.dialog.open(ClinicalSubCategoryDetailsComponent, {
      width: '500px',
      height: 'auto',
      data: {
        subcategoryValueFromItem: subcategory,
        categoryId: this.clinicalItemForm.get('categoryId')?.value
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        const categoryID = this.clinicalItemForm.get('categoryId')?.value;
        this._lookupService.getSubCategory(categoryID)
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe((data) => {
            this.clinicalSubCategoryddl = data || [];

            this.clinicalSubCategoryCtrl.setValue('');

            this._initializeSubCategoryAutocomplete(categoryID);

            const added = this.clinicalSubCategoryddl.find(c =>
              c.id === response.result.id ||
              c.name?.toLowerCase() === response.result.name?.toLowerCase()
            );

            if (added) {
              this.clinicalSubCategoryCtrl.setValue(added);
              this.clinicalItemForm.patchValue({ subcategoryId: added.id });
            }
          });
      }
    });
  }

  loadSubcategories(categoryId: number) {
    this._lookupService.getSubCategory(categoryId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.clinicalSubCategoryddl = data || [];
        this._initializeSubCategoryAutocomplete(categoryId);

        if (this.isEdit && this._dataupdate.clinicalitems) {
          const matchedsubCategory = this.clinicalSubCategoryddl.find(
            s => s.id === this._dataupdate.clinicalitems.subCategoryId
          );
          if (matchedsubCategory) {
            this.clinicalSubCategoryCtrl.setValue(matchedsubCategory);
          }
        }
      });
  }

  private _initializeSubCategoryAutocomplete(categoryId?: number) {
    this.filteredclinicalSubCategory$ = this.clinicalSubCategoryCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const search = typeof value === 'string' ? value : value?.name || '';

        return this._lookupService.getSubCategory(Number(categoryId), search).pipe(
          tap(list => {
            this.showAddOptionforSubCategory =
              !!search && !list.some(x => x.name.toLowerCase() === search.toLowerCase());
          }),
          map(list => list) // return backend list directly
        );
      })
    );
  }

  private clinicalItemDatafill(): void {
    let categoryId: number | null = null;
    let subCategoryId: number | null = null;

    if (this.isEdit && this._dataupdate.clinicalitems) {
      categoryId = this._dataupdate.clinicalitems.categoryId;
      subCategoryId = this._dataupdate.clinicalitems.subCategoryId;
      this.clinicalItemForm.patchValue({
        description: this._dataupdate.clinicalitems.description,
        additionalInfo: this._dataupdate.clinicalitems.additionalInfo,
        isSince: this._dataupdate.clinicalitems.isSince,
      });
    }

    if (!this.isEdit && this._dataupdate.fromOPDcategoryvalue) {
      categoryId = this._dataupdate.fromOPDcategoryvalue.id;
      subCategoryId = this._dataupdate.fromOPDSubcategoryvalue?.id || null;
    }

    if (!categoryId) {
      return;
    }

    this.clinicalItemForm.patchValue({
      categoryId: categoryId,
      subcategoryId: subCategoryId
    });

    const matchedCategory = this.clinicalCategoryddl.find(c => c.id === categoryId);
    this.clinicalCategoryCtrl.setValue(matchedCategory);
    this.loadSubCategoriesAndSet(categoryId, subCategoryId);
  }

  private loadSubCategoriesAndSet(categoryId: number, subCategoryId: number | null): void {
    if (!categoryId) {
      return;
    }

    this._lookupService.getSubCategory(categoryId).subscribe((data) => {
      this.clinicalSubCategoryddl = data || [];
      this._initializeSubCategoryAutocomplete(categoryId);

      if (subCategoryId) {
        const matchedSub = this.clinicalSubCategoryddl.find(s => s.id === subCategoryId);
        if (matchedSub) {
          this.clinicalSubCategoryCtrl.setValue(matchedSub);
        }
      }
    });
  }

}
